export type TOrganizationType =
  | 'bank'
  | 'microcredit_company'
  | 'insurance_company'
  | 'exchange_office'
  | 'money_transfer'
  | 'crypto_company'

export type TProductType = 'credit' | 'deposit' | 'card'

/**
 * Value of the first select: either an organization type (the organization
 * itself is reviewed) or a product type (one of its products is reviewed).
 */
export type TReviewTarget = TOrganizationType | TProductType

export interface IOrganizationTypeConfig {
  /** i18n key for the select option label */
  labelKey: string
  /** MeiliSearch index name, used without the environment prefix */
  meiliIndex: string
  /** Show the whole list at once instead of paging it. Defaults to true. */
  paginated?: boolean
}

// A type stays listed even without a criteria set on the backend
// (`exchange_office` today) — the form explains that instead of hiding it.
export const ORG_TYPE_CONFIG: Record<TOrganizationType, IOrganizationTypeConfig> = {
  bank: {
    labelKey: 'common.banks',
    meiliIndex: 'banks',
    paginated: false
  },
  microcredit_company: {
    labelKey: 'common.microcredit_companies',
    meiliIndex: 'microcredit_companies'
  },
  insurance_company: {
    labelKey: 'common.insurance_companies',
    meiliIndex: 'insurance_companies'
  },
  exchange_office: {
    labelKey: 'common.exchange_offices',
    meiliIndex: 'exchange_offices'
  },
  money_transfer: {
    labelKey: 'common.money_transfers',
    meiliIndex: 'money_transfers'
  },
  crypto_company: {
    labelKey: 'common.crypto_companies',
    meiliIndex: 'crypto_companies'
  }
}

export interface IProductTypeConfig {
  /** i18n key for the select option label */
  labelKey: string
  /** MeiliSearch index name, used without the environment prefix */
  meiliIndex: string
  /**
   * Hand-maintained on purpose: staging data is incomplete, so deriving this
   * from the API would be wrong. A single entry skips the org-type step.
   */
  orgTypes: TOrganizationType[]
}

export const PRODUCT_TYPE_CONFIG: Record<TProductType, IProductTypeConfig> = {
  credit: {
    labelKey: 'feature.reviews_add_form.product_types.credit',
    meiliIndex: 'credits',
    orgTypes: ['bank', 'microcredit_company']
  },
  deposit: {
    labelKey: 'feature.reviews_add_form.product_types.deposit',
    meiliIndex: 'deposits',
    orgTypes: ['bank', 'microcredit_company']
  },
  card: {
    labelKey: 'feature.reviews_add_form.product_types.card',
    meiliIndex: 'cards',
    orgTypes: ['bank']
  }
}

export const isProductType = (value: string): value is TProductType =>
  value in PRODUCT_TYPE_CONFIG

export const isOrganizationType = (value: string): value is TOrganizationType =>
  value in ORG_TYPE_CONFIG
